import { useCallback, useEffect, useMemo, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { formatRelativeUs, formatTimestampUs, shortId } from '../../lib/format';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import '../_shared/shared.css';
import './diagnostics.css';
import {
  cancelDiagnosticLock,
  createDiagnosticLock,
  effectiveLockStatus,
  listDiagnosticLocks,
  type DiagnosticLock,
  type DiagnosticTargetType,
} from './diagnosticLocksApi';

// 狀態中文標籤(與 DiagnosticLocksPage 同款;英文 status 值只留在 API 層)
const STATUS_LABEL: Partial<Record<DiagnosticLock['status'], string>> = {
  active: '使用中',
  cancelled: '已取消',
  expired: '已到期',
};

interface Props {
  targetType: DiagnosticTargetType;
  targetId: string;
}

export function DiagnosticLockPanel({ targetType, targetId }: Props) {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const [locks, setLocks] = useState<DiagnosticLock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [ttl, setTtl] = useState(60);
  const [busy, setBusy] = useState(false);
  const [stop, setStop] = useState<DiagnosticLock | null>(null);
  const active = useMemo(() => locks.find((l) => effectiveLockStatus(l) === 'active') ?? null, [locks]);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setError(null);
    try {
      const page = await listDiagnosticLocks({ targetType, targetId, status: 'active', limit: 20 });
      setLocks(page.locks);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId]);

  useEffect(() => {
    void load();
  }, [load]);

  const canCreate = writable && !active && reason.trim() !== '' && caseId.trim() !== '';

  async function submit() {
    if (!canCreate) return;
    setBusy(true);
    setError(null);
    try {
      await createDiagnosticLock({ targetType, targetId, ttlMinutes: ttl, reason: reason.trim(), caseId: caseId.trim() });
      setReason('');
      setCaseId('');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function confirmStop() {
    if (!stop) return;
    setBusy(true);
    setError(null);
    try {
      await cancelDiagnosticLock(stop.lock_id, '從管理主控台停止', stop.case_id);
      setStop(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card diag-panel">
      <div className="diag-panel-head">
        <h2 className="adm-card-title">診斷鎖定</h2>
        <button className="btn-ghost btn-sm" onClick={() => void load()} disabled={loading}>
          重新整理
        </button>
      </div>

      {loading && locks.length === 0 && <LoadingState label="載入診斷鎖定…" />}
      {error != null && <ErrorState error={error} onRetry={() => void load()} />}

      {!loading && error == null && (
        <>
          {active ? (
            <div className="diag-active">
              <div className="diag-active-head">
                <div>
                  <div className="diag-scope mono">{active.diagnostic_scope_id}</div>
                  <div className="text-secondary">
                    {active.case_id || '無案件'} · 到期 {formatRelativeUs(active.expires_at)}
                  </div>
                </div>
                {writable && (
                  <button className="btn-ghost btn-sm" onClick={() => setStop(active)} disabled={busy}>
                    停止
                  </button>
                )}
              </div>
              <code className="adm-devlog-output">{active.log_query}</code>
            </div>
          ) : (
            <div className="text-secondary">目前沒有使用中的鎖定。</div>
          )}

          {writable && !active && (
            <div className="diag-form diag-form--panel">
              <label>
                <span>存續時間</span>
                <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>
                  <option value={15}>15 分鐘</option>
                  <option value={60}>60 分鐘</option>
                  <option value={240}>4 小時</option>
                  <option value={1440}>24 小時</option>
                </select>
              </label>
              <label>
                <span>案件編號</span>
                <input value={caseId} onChange={(e) => setCaseId(e.target.value)} placeholder="CS-..." />
              </label>
              <label className="diag-reason">
                <span>原因</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="調查摘要" />
              </label>
              <button className="btn-primary btn-sm" onClick={() => void submit()} disabled={!canCreate || busy}>
                建立
              </button>
            </div>
          )}

          {locks.length > 0 && (
            <div className="diag-mini-list">
              {locks.map((l) => (
                <div className="diag-mini-row" key={l.lock_id}>
                  <span className="pill pill-dot pill-info">{STATUS_LABEL[effectiveLockStatus(l)] ?? effectiveLockStatus(l)}</span>
                  <span className="mono" title={l.lock_id}>
                    {shortId(l.lock_id, 12, 4)}
                  </span>
                  <span className="text-tertiary">{formatTimestampUs(l.created_at, { second: undefined })}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {stop && (
        <ConfirmDialog
          title="停止診斷鎖定"
          body={
            <span>
              停止 <span className="mono">{stop.diagnostic_scope_id}</span>。
            </span>
          }
          confirmLabel="停止"
          tone="warn"
          busy={busy}
          onCancel={() => setStop(null)}
          onConfirm={() => void confirmStop()}
        />
      )}
    </section>
  );
}
